/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React from 'react';

import { trackAddProductClick, trackDrawerClose, trackDrawerOpen } from '../../Roofing/Shingles/analytics';
import { makeT } from '../../location/locales';
import BottomDrawer from '../BottomDrawer';

const tEn = makeT('en-US');

const ComparisonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ItemsList = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  overflow-x: auto;
  padding: 4px 0;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
    overflow-x: visible;
  }
`;

const AddProductButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #ffffff;
  border: 2px dashed #cccccc;
  border-radius: 4px;
  padding: 12px;
  width: 280px;
  height: 80px;
  flex-shrink: 0;
  cursor: pointer;
  font-family: 'OCRoboto';
  font-size: 14px;
  color: #000000;
  transition: all 0.2s ease;

  &:hover {
    border-color: #999999;
    color: #333333;
  }

  &::before {
    content: '+';
    font-size: 20px;
    font-weight: bold;
  }

  @media (max-width: 767px) {
    width: 100%;
    min-height: 80px;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: space-between;
    flex-shrink: 0;
  }
`;

const CompareButton = styled.button`
  background: ${props => (props.disabled ? '#cccccc' : '#d40f7d')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  font-family: 'OCRoboto';
  font-size: 16px;
  font-weight: 400;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #b50d68;
  }
`;

const ClearAllButton = styled.button`
  background: transparent;
  border: none;
  color: #000000;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #333333;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666666;
  font-style: italic;
`;

const ComparisonDrawer = ({
  items = [],
  maxItems = 3,
  onClearAll,
  onRemoveItem,
  onCompare,
  renderItem,
  compareButtonText = count => `Compare (${count})`,
  clearAllText = 'Clear All',
  addProductText = 'Add Product',
  emptyStateContent = 'No items selected for comparison',
  isExpanded = false,
  onToggleExpanded,
  headerText = 'Compare up to three products',
  className = '',
  ...props
}) => {
  const canAddMore = items.length < maxItems;
  const canCompare = items.length >= 2;

  const handleCompare = e => {
    if (canCompare && onCompare) {
      onCompare(items, e);
    }
  };

  const handleClearAll = e => {
    if (onClearAll) {
      onClearAll(e);
    }
  };

  const handleAddProduct = e => {
    if (canAddMore && onToggleExpanded) {
      // Track analytics
      trackAddProductClick({
        element: e.currentTarget,
        translatedClickText: tEn('shingles.comparison.drawer.add_product'),
      });
      // Collapse the drawer so user can select another product
      onToggleExpanded();
    }
  };

  const handleToggle = e => {
    if (isExpanded) {
      trackDrawerClose({ element: e?.currentTarget });
    } else {
      trackDrawerOpen({ element: e?.currentTarget });
    }
    if (onToggleExpanded) {
      onToggleExpanded();
    }
  };

  // Don't render if no items
  if (items.length === 0) {
    return null;
  }

  // Determine header text based on expanded state
  const displayHeaderText = headerText;

  return (
    <BottomDrawer
      isExpanded={isExpanded}
      onToggle={handleToggle}
      headerContent={displayHeaderText}
      className={className}
      {...props}
    >
      <ComparisonContent>
        <ItemsList>
          {items.map((item, index) => (
            <div key={item.uuid || item.id || index}>
              {renderItem ? (
                renderItem(item, onRemoveItem)
              ) : (
                <div>
                  Item {index + 1}
                  <button type="button" onClick={() => onRemoveItem(item.uuid || item.id)}>
                    ×
                  </button>
                </div>
              )}
            </div>
          ))}

          {canAddMore && (
            <AddProductButton onClick={handleAddProduct} disabled={!canAddMore} aria-label={addProductText}>
              {addProductText}
            </AddProductButton>
          )}
        </ItemsList>

        <ActionsSection>
          <CompareButton
            onClick={handleCompare}
            disabled={!canCompare}
            aria-label={canCompare ? compareButtonText(items.length) : 'Select at least 2 items to compare'}
          >
            {compareButtonText(items.length)}
          </CompareButton>

          <ClearAllButton onClick={handleClearAll} aria-label={clearAllText}>
            {clearAllText}
          </ClearAllButton>
        </ActionsSection>
      </ComparisonContent>

      {items.length === 0 && <EmptyState>{emptyStateContent}</EmptyState>}
    </BottomDrawer>
  );
};

ComparisonDrawer.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  maxItems: PropTypes.number,
  onClearAll: PropTypes.func,
  onRemoveItem: PropTypes.func,
  onCompare: PropTypes.func,
  renderItem: PropTypes.func,
  compareButtonText: PropTypes.func,
  clearAllText: PropTypes.string,
  addProductText: PropTypes.string,
  emptyStateContent: PropTypes.string,
  isExpanded: PropTypes.bool,
  onToggleExpanded: PropTypes.func,
  headerText: PropTypes.string,
  className: PropTypes.string,
};

ComparisonDrawer.defaultProps = {
  items: [],
  maxItems: 3,
  onClearAll: null,
  onRemoveItem: null,
  onCompare: null,
  renderItem: null,
  compareButtonText: count => `Compare (${count})`,
  clearAllText: 'Clear All',
  addProductText: 'Add Product',
  emptyStateContent: 'No items selected for comparison',
  isExpanded: false,
  onToggleExpanded: null,
  headerText: 'Compare up to three products',
  className: '',
};

export default ComparisonDrawer;
